import { AudioDelay, GameSettings } from '@/common/types/student';
import { SettingChange } from '@/common/types/studentEvents';
import { capitalize } from 'lodash';

// Safe path access without mutating the original path array.
// Handles both objects and arrays (numeric segment = array index).
function getValueAtPath(obj: any, path: string[]): unknown {
  let current = obj;
  for (const seg of path) {
    if (current == null) return undefined;
    const isArr = Array.isArray(current);
    const key = isArr && /^\d+$/.test(seg) ? Number(seg) : seg;
    if (!(key in current)) return undefined;
    current = current[key];
  }
  return current;
}

function hasPath(obj: any, path: string[]): boolean {
  let current = obj;
  for (const seg of path) {
    if (current == null) return false;
    const isArr = Array.isArray(current);
    const key = isArr && /^\d+$/.test(seg) ? Number(seg) : seg;
    if (!(key in current)) return false;
    current = current[key];
  }
  return true;
}

export function getSettingsChanges(
  prevSettings: GameSettings,
  newSettings: GameSettings,
) {
  // Walk the newSettings and compare against prevSettings.
  function collectChanges(path: string[], value: unknown) {
    // Handle primitives as base case
    if (
      typeof value === 'boolean' ||
      typeof value === 'string' ||
      typeof value === 'number'
    ) {
      const newStr = value.toString();
      if (hasPath(prevSettings, path)) {
        const prevVal = getValueAtPath(prevSettings, path);
        const prevStr =
          prevVal !== undefined && prevVal !== null ? prevVal.toString() : '';
        if (prevStr !== newStr) {
          changes.push(
            getSettingDisplayValues(path[path.length - 1], prevStr, newStr),
          );
        }
      } else {
        changes.push(
          getSettingDisplayValues(path[path.length - 1], '', newStr),
        );
      }
      return;
    }

    // Recurse objects/arrays
    if (value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) {
        collectChanges([...path, k], v);
      }
    }
  }

  const changes: SettingChange[] = [];

  // Begin comparison from each top-level key in "settings"
  for (const [k, v] of Object.entries(
    newSettings as Record<keyof GameSettings, unknown>,
  )) {
    collectChanges([k], v);
  }

  return changes;
}

export function getSettingDisplayValues(
  setting: string,
  oldValue: boolean | string | number,
  newValue: boolean | string | number,
): SettingChange {
  let displaySetting: string;
  let getDisplayValue: (value: string | number | boolean) => string;
  switch (setting) {
    case 'playPhonemeOnCardDrop':
      displaySetting = 'Play phoneme on card drop';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'controllerType':
      displaySetting = 'Controller type';
      getDisplayValue = (value) => capitalize(value as string);
      break;

    case 'duelsEnabled':
      displaySetting = 'Duels enabled';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'audioDelay':
      displaySetting = 'Audio delay';
      getDisplayValue = (value: string | number | boolean) => {
        const num = typeof value === 'string' ? Number(value) : value;
        switch (num) {
          case AudioDelay.SHORT:
            return 'Short';
          case AudioDelay.MEDIUM:
            return 'Medium';
          case AudioDelay.LONG:
            return 'Long';
          default:
            return value.toString() + 'ms';
        }
      };
      break;

    case 'distractorCardsEnabled':
      displaySetting = 'Distractor cards';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'wordLimit':
      displaySetting = 'Word limit';
      getDisplayValue = (value) => value.toString();
      break;

    case 'wordSegmentationEnabled':
      displaySetting = 'Word segmentation';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'articulatoryGesturesEnabled':
      displaySetting = 'Articulatory gestures';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'sfxOn':
      displaySetting = 'SFX';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    case 'musicOn':
      displaySetting = 'Music';
      getDisplayValue = (value) => (value ? 'Enabled' : 'Disabled');
      break;

    default:
      throw new Error(`Invalid setting: ${setting}`);
  }
  return {
    setting: displaySetting,
    oldValue: getDisplayValue(oldValue),
    newValue: getDisplayValue(newValue),
  };
}
